import { API_URL, authHeaders, authOnlyHeaders } from "./api";

export const REMINDER_KEY = "alphanote-reminders";

export interface ReminderRecord {
  id: string;
  docId: string;
  date: string;
  endDate: string;
  hasEndDate: boolean;
  hasTime: boolean;
  timeFormat: string;
  reminder: string;
  notified: boolean;
}

export function readReminders(): Record<string, ReminderRecord> {
  if (typeof window === "undefined") return {};
  try {
    const value = JSON.parse(window.localStorage.getItem(REMINDER_KEY) || "{}");
    return value && typeof value === "object" ? value : {};
  } catch {
    return {};
  }
}

let migration: { token: string; promise: Promise<boolean> } | null = null;

/** 사용자가 명시적으로 동의한 기존 브라우저 레코드만 현재 계정으로 이관한다. */
export function migrateLocalReminders(accessToken?: string): Promise<boolean> {
  if (typeof window === "undefined" || !accessToken) return Promise.resolve(false);
  if (migration?.token === accessToken) return migration.promise;

  const records = Object.values(readReminders());
  if (records.length === 0) {
    window.localStorage.removeItem(REMINDER_KEY);
    return Promise.resolve(true);
  }

  const promise = (async () => {
    try {
      const response = await fetch(`${API_URL}/reminders/migrate`, {
        method: "POST",
        credentials: "include",
        headers: authHeaders(accessToken),
        body: JSON.stringify({ records }),
      });
      if (!response.ok) return false;
      window.localStorage.removeItem(REMINDER_KEY);
      return true;
    } catch {
      return false;
    }
  })();

  migration = { token: accessToken, promise };
  void promise.then((ok) => {
    if (!ok && migration?.promise === promise) migration = null;
  });
  return promise;
}

export function discardLocalReminders(): void {
  if (typeof window !== "undefined") window.localStorage.removeItem(REMINDER_KEY);
}

export async function upsertReminder(
  record: ReminderRecord,
  accessToken?: string,
): Promise<boolean> {
  if (!accessToken) return false;
  try {
    const response = await fetch(`${API_URL}/reminders/${encodeURIComponent(record.id)}`, {
      method: "PUT",
      credentials: "include",
      headers: authHeaders(accessToken),
      body: JSON.stringify(record),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function removeReminder(
  id: string,
  docId: string,
  accessToken?: string,
): Promise<boolean> {
  if (!accessToken) return false;
  try {
    const response = await fetch(
      `${API_URL}/reminders/${encodeURIComponent(id)}?docId=${encodeURIComponent(docId)}`,
      {
        method: "DELETE",
        credentials: "include",
        headers: authOnlyHeaders(accessToken),
      },
    );
    return response.ok;
  } catch {
    return false;
  }
}

export function formatTimeValue(h: number, m: number, is24h: boolean): string {
  const mm = m.toString().padStart(2, "0");
  if (is24h) return `${h.toString().padStart(2, "0")}:${mm}`;
  const ampm = h >= 12 ? "오후" : "오전";
  const hh = h % 12 || 12;
  return `${ampm} ${hh}:${mm}`;
}
